/*
  Framar store locator (<fmr-store-locator>).
  Rebuilt from the legacy find-framar section without jQuery/turf/jquery.csv.
  Google Maps JS (places lib) is loaded lazily on first interaction with the
  search box, so the initial page paint costs nothing extra.
  Store data comes from a CSV file (columns: name,address_1,address_2,city,
  state,postal_code,country,phone,website,email,online,lat,long).
*/
(() => {
  'use strict';

  const MAX_RESULTS = 100;
  /* Search radius (km) by the kind of place the visitor picked */
  const RADIUS_BY_PLACE_TYPE = {
    country: 5000,
    administrative_area_level_1: 300,
    locality: 100,
    default: 50,
  };

  let mapsPromise = null;
  function loadGoogleMaps(key) {
    if (mapsPromise) return mapsPromise;
    mapsPromise = new Promise((resolve, reject) => {
      if (window.google && window.google.maps && window.google.maps.places) {
        resolve(window.google.maps);
        return;
      }
      const cb = '__fmrMapsReady_' + Math.random().toString(36).slice(2);
      window[cb] = () => {
        delete window[cb];
        resolve(window.google.maps);
      };
      const s = document.createElement('script');
      s.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(key) +
        '&libraries=places&loading=async&callback=' + cb;
      s.async = true;
      s.onerror = () => reject(new Error('Google Maps failed to load'));
      document.head.appendChild(s);
    });
    return mapsPromise;
  }

  /* Minimal RFC-4180-ish CSV parser (quoted fields, escaped quotes, CRLF) */
  function parseCsv(text) {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += c;
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === ',') {
        row.push(field); field = '';
      } else if (c === '\n' || c === '\r') {
        if (c === '\r' && text[i + 1] === '\n') i++;
        row.push(field); field = '';
        if (row.length > 1 || row[0] !== '') rows.push(row);
        row = [];
      } else field += c;
    }
    row.push(field);
    if (row.length > 1 || row[0] !== '') rows.push(row);
    if (!rows.length) return [];
    const headers = rows[0].map((h) => h.trim().toLowerCase());
    return rows.slice(1).map((r) => {
      const obj = {};
      headers.forEach((h, idx) => (obj[h] = (r[idx] || '').trim()));
      return obj;
    });
  }

  function haversineKm(lat1, lon1, lat2, lon2) {
    const rad = Math.PI / 180;
    const dLat = (lat2 - lat1) * rad;
    const dLon = (lon2 - lon1) * rad;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(dLon / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function esc(str) {
    const d = document.createElement('div');
    d.textContent = str == null ? '' : String(str);
    return d.innerHTML;
  }

  class FmrStoreLocator extends HTMLElement {
    connectedCallback() {
      this.mapsKey = this.dataset.mapsKey;
      this.csvUrl = this.dataset.csvUrl;
      this.stores = [];
      this.onlineStores = [];
      this.markers = [];
      this.map = null;
      this.infoWindow = null;
      this.lastPlaceType = 'default';
      this.activeSuggestion = -1;

      this.input = this.querySelector('[data-locator-input]');
      this.clearBtn = this.querySelector('[data-locator-clear]');
      this.geoBtn = this.querySelector('[data-locator-geolocate]');
      this.suggestions = this.querySelector('[data-locator-suggestions]');
      this.resultsEl = this.querySelector('[data-locator-results]');
      this.statusEl = this.querySelector('[data-locator-status]');
      this.introEl = this.querySelector('[data-locator-intro]');
      this.introMapEl = this.querySelector('[data-locator-intro-map]');
      this.mapEl = this.querySelector('[data-locator-map]');
      this.tabsEl = this.querySelector('[data-locator-tabs]');

      if (!this.input || !this.mapsKey || !this.csvUrl) return;

      this.input.addEventListener('focus', () => this.bootstrap());
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => this.onKeydown(e));
      this.clearBtn?.addEventListener('click', () => this.reset());
      this.geoBtn?.addEventListener('click', () => this.useMyLocation());
      this.tabsEl?.querySelectorAll('[data-locator-tab]').forEach((btn) => {
        btn.addEventListener('click', () => this.showPanel(btn.dataset.locatorTab));
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) this.hideSuggestions();
      });
    }

    /* Lazy: first interaction loads Maps + the CSV in parallel */
    bootstrap() {
      if (this.bootstrapped) return this.ready;
      this.bootstrapped = true;
      const csv = fetch(this.csvUrl)
        .then((r) => {
          if (!r.ok) throw new Error('CSV fetch failed: ' + r.status);
          return r.text();
        })
        .then((text) => {
          const all = parseCsv(text);
          this.onlineStores = all.filter((s) => s.online);
          this.stores = all.filter((s) => !s.online);
        });
      this.ready = Promise.all([loadGoogleMaps(this.mapsKey), csv])
        .then(() => {
          this.autocompleteService = new google.maps.places.AutocompleteService();
          this.geocoder = new google.maps.Geocoder();
        })
        .catch((err) => {
          console.error('[fmr-store-locator]', err);
          this.setStatus(this.dataset.textError || 'The store locator could not load. Please try again later.');
        });
      return this.ready;
    }

    ensureMap() {
      if (this.map) return;
      this.map = new google.maps.Map(this.mapEl, {
        zoom: 11,
        center: { lat: 43.09, lng: -79.08 },
        mapTypeControl: false,
        streetViewControl: false,
      });
      this.placesService = new google.maps.places.PlacesService(this.map);
      this.infoWindow = new google.maps.InfoWindow();
      let t;
      this.map.addListener('center_changed', () => {
        clearTimeout(t);
        t = setTimeout(() => this.renderForCenter(), 500);
      });
    }

    onInput() {
      const q = this.input.value.trim();
      this.clearBtn?.toggleAttribute('hidden', q === '');
      if (!q) return this.hideSuggestions();
      this.bootstrap().then(() => {
        if (!this.autocompleteService) return;
        this.autocompleteService.getPlacePredictions(
          { input: q, types: ['establishment', 'geocode'] },
          (preds, status) => this.renderSuggestions(preds, status)
        );
      });
    }

    onKeydown(e) {
      const items = [...this.suggestions.querySelectorAll('[data-place-id]')];
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (!items.length) return;
        e.preventDefault();
        this.activeSuggestion =
          (this.activeSuggestion + (e.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
        items.forEach((el, i) => el.classList.toggle('is-active', i === this.activeSuggestion));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const pick = items[this.activeSuggestion >= 0 ? this.activeSuggestion : 0];
        if (pick) this.selectPlace(pick.dataset.placeId);
      } else if (e.key === 'Escape') {
        this.hideSuggestions();
      }
    }

    renderSuggestions(preds, status) {
      this.activeSuggestion = -1;
      this.suggestions.innerHTML = '';
      if (status !== google.maps.places.PlacesServiceStatus.OK || !preds?.length) {
        this.hideSuggestions();
        return;
      }
      preds.forEach((p) => {
        const li = document.createElement('li');
        li.className = 'fmr-locator__suggestion';
        li.setAttribute('role', 'option');
        li.dataset.placeId = p.place_id;
        li.innerHTML =
          '<span class="fmr-locator__suggestion-main">' + esc(p.structured_formatting.main_text) + '</span>' +
          (p.structured_formatting.secondary_text
            ? '<span class="fmr-locator__suggestion-secondary">' + esc(p.structured_formatting.secondary_text) + '</span>'
            : '');
        li.addEventListener('click', () => this.selectPlace(p.place_id));
        this.suggestions.appendChild(li);
      });
      this.suggestions.hidden = false;
    }

    hideSuggestions() {
      if (this.suggestions) {
        this.suggestions.hidden = true;
        this.suggestions.innerHTML = '';
      }
      this.activeSuggestion = -1;
    }

    selectPlace(placeId) {
      this.ensureMap();
      this.placesService.getDetails({ placeId }, (place, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.geometry) return;
        const country = place.address_components?.find((c) => c.types.includes('country'));
        this.countryCode = country ? country.short_name : null;
        this.lastPlaceType = place.types?.[0] || 'default';
        this.input.value = place.formatted_address || this.input.value;
        this.hideSuggestions();
        this.startSearchUi();
        this.map.setCenter(place.geometry.location);
        this.map.setZoom(11);
        this.renderForCenter();
      });
    }

    useMyLocation() {
      if (!navigator.geolocation) return;
      this.setStatus(this.dataset.textLocating || 'Finding your location…');
      this.bootstrap().then(() => {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            this.geocoder.geocode({ location: loc }, (res, status) => {
              this.setStatus('');
              if (status === 'OK' && res?.[0]) {
                const country = res[0].address_components.find((c) => c.types.includes('country'));
                this.countryCode = country ? country.short_name : null;
                this.input.value = res[0].formatted_address;
                this.clearBtn?.removeAttribute('hidden');
              }
              this.lastPlaceType = 'locality';
              this.ensureMap();
              this.startSearchUi();
              this.map.setCenter(loc);
              this.map.setZoom(11);
              this.renderForCenter();
            });
          },
          () => this.setStatus(this.dataset.textGeoDenied || 'We could not access your location.')
        );
      });
    }

    startSearchUi() {
      this.classList.add('fmr-locator--searching');
      if (this.introEl) this.introEl.hidden = true;
      if (this.introMapEl) this.introMapEl.hidden = true;
      this.resultsEl.hidden = false;
      this.tabsEl?.removeAttribute('hidden');
      this.showPanel(window.matchMedia('(min-width: 750px)').matches ? 'both' : 'list');
    }

    showPanel(which) {
      if (which === 'both') {
        this.mapEl.parentElement.classList.add('is-visible');
        return;
      }
      this.tabsEl?.querySelectorAll('[data-locator-tab]').forEach((btn) => {
        const active = btn.dataset.locatorTab === which;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-selected', active ? 'true' : 'false');
      });
      this.classList.toggle('fmr-locator--show-map', which === 'map');
      this.mapEl.parentElement.classList.add('is-visible');
      if (which === 'map') google.maps.event.trigger(this.map, 'resize');
    }

    radiusKm() {
      return RADIUS_BY_PLACE_TYPE[this.lastPlaceType] || RADIUS_BY_PLACE_TYPE.default;
    }

    renderForCenter() {
      if (!this.map) return;
      const center = this.map.getCenter();
      const radius = this.radiusKm();
      const nearby = [];
      this.stores.forEach((s) => {
        const lat = parseFloat(s.lat);
        const lng = parseFloat(s.long);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return;
        const d = haversineKm(lat, lng, center.lat(), center.lng());
        if (d <= radius) nearby.push({ ...s, _distance: d });
      });
      nearby.sort((a, b) => a._distance - b._distance);

      const online = this.countryCode
        ? this.onlineStores.filter((s) => (s.country || '').toUpperCase() === this.countryCode.toUpperCase() || (s.country || '').toUpperCase() === this.countryName())
        : [];

      this.clearMarkers();
      this.resultsEl.innerHTML = '';
      const toShow = nearby.slice(0, MAX_RESULTS);

      if (!toShow.length && !online.length) {
        this.setStatus(this.dataset.textNone || 'No stores found around there — try a nearby city.');
        return;
      }
      this.setStatus('');

      online.forEach((s) => this.resultsEl.appendChild(this.buildCard(s, null)));
      toShow.forEach((s, i) => {
        this.resultsEl.appendChild(this.buildCard(s, i + 1));
        this.addMarker(s, i + 1);
      });
    }

    countryName() {
      /* CSV sometimes stores full names; map the common ones */
      const names = { CA: 'CANADA', US: 'UNITED STATES' };
      return names[(this.countryCode || '').toUpperCase()] || '';
    }

    buildCard(s, index) {
      const li = document.createElement('li');
      li.className = 'fmr-locator__result';
      const km = s._distance != null ? s._distance.toFixed(1) : null;
      const lines = [];
      if (s.address_1) lines.push(esc(s.address_1));
      if (s.address_2) lines.push(esc(s.address_2));
      const cityBits = [s.city, s.state, s.postal_code].filter(Boolean).map(esc).join(', ');
      if (cityBits) lines.push(cityBits);
      if (s.country) lines.push(esc(s.country));

      li.innerHTML =
        '<div class="fmr-locator__result-head">' +
        '<span class="fmr-locator__result-name">' + (index ? index + '. ' : '') + esc(s.name) + '</span>' +
        (km ? '<span class="fmr-locator__badge">' + km + ' km</span>' : '') +
        (index ? '' : '<span class="fmr-locator__badge fmr-locator__badge--online">' + (this.dataset.textOnline || 'online') + '</span>') +
        '</div>' +
        '<address class="fmr-locator__result-address">' + lines.join('<br>') + '</address>' +
        '<p class="fmr-locator__result-contact">' +
        (s.phone ? '<a href="tel:' + esc(s.phone.replace(/[^+\d]/g, '')) + '">' + esc(s.phone) + '</a>' : '') +
        (s.website ? '<a href="' + esc(s.website) + '" target="_blank" rel="noopener">' + (this.dataset.textWebsite || 'Website') + '</a>' : '') +
        (s.email ? '<a href="mailto:' + esc(s.email) + '">' + esc(s.email) + '</a>' : '') +
        '</p>' +
        (index && s.lat && s.long
          ? '<p class="fmr-locator__result-actions">' +
            '<a href="https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(s.lat + ',' + s.long) + '" target="_blank" rel="noopener">' + (this.dataset.textDirections || 'Get directions') + '</a>' +
            '<button type="button" class="fmr-locator__view-map" data-lat="' + esc(s.lat) + '" data-lng="' + esc(s.long) + '">' + (this.dataset.textViewMap || 'View on map') + '</button>' +
            '</p>'
          : '');

      li.querySelector('.fmr-locator__view-map')?.addEventListener('click', (e) => {
        const { lat, lng } = e.currentTarget.dataset;
        this.showPanel('map');
        this.map.setCenter({ lat: parseFloat(lat), lng: parseFloat(lng) });
        this.map.setZoom(15);
      });
      return li;
    }

    addMarker(s, index) {
      const marker = new google.maps.Marker({
        position: { lat: parseFloat(s.lat), lng: parseFloat(s.long) },
        map: this.map,
        icon: this.dataset.markerUrl
          ? { url: this.dataset.markerUrl, scaledSize: new google.maps.Size(30, 45), anchor: new google.maps.Point(15, 45) }
          : undefined,
        title: s.name,
      });
      marker.addListener('click', () => {
        const html =
          '<div class="fmr-locator__iw"><strong>' + (index ? index + '. ' : '') + esc(s.name) + '</strong><br>' +
          [s.address_1, s.address_2, [s.city, s.state, s.postal_code].filter(Boolean).join(', '), s.country]
            .filter(Boolean).map(esc).join('<br>') +
          (s.phone ? '<br>' + esc(s.phone) : '') +
          '</div>';
        this.infoWindow.setContent(html);
        this.infoWindow.open(this.map, marker);
      });
      this.markers.push(marker);
    }

    clearMarkers() {
      this.markers.forEach((m) => m.setMap(null));
      this.markers = [];
    }

    setStatus(msg) {
      if (this.statusEl) this.statusEl.textContent = msg || '';
    }

    reset() {
      this.input.value = '';
      this.clearBtn?.setAttribute('hidden', '');
      this.hideSuggestions();
      this.setStatus('');
      this.classList.remove('fmr-locator--searching', 'fmr-locator--show-map');
      if (this.introEl) this.introEl.hidden = false;
      if (this.introMapEl) this.introMapEl.hidden = false;
      this.resultsEl.hidden = true;
      this.resultsEl.innerHTML = '';
      this.tabsEl?.setAttribute('hidden', '');
      this.mapEl.parentElement.classList.remove('is-visible');
      this.clearMarkers();
      this.input.focus();
    }
  }

  if (!customElements.get('fmr-store-locator')) {
    customElements.define('fmr-store-locator', FmrStoreLocator);
  }
})();
